import { CerrixPromptService } from "./../../../../../shared/services/cerrix-prompt.service";
import { Component, inject } from "@angular/core";
import { ActiveToast, ToastrService } from "ngx-toastr";
import { DevManagementDataService } from "@app/admin/services/devmanagement.service";
import { Observable } from "rxjs";
import { finalize } from "rxjs/operators";
import { GenericListFieldType } from "@app/shared/models/GenericList/GenericListField";

@Component({
    selector: "dev-actions",
    templateUrl: "./dev-actions.component.html",
    styleUrls: ["./dev-actions.component.scss"],
})
export class DevActionsComponent {
    private dataService = inject(DevManagementDataService);
    private promptService = inject(CerrixPromptService);
    private _toastr = inject(ToastrService);

    private prompt;
    private toast: ActiveToast<any>;

    private visualizeAction(
        action: Observable<any>,
        progressMessage: string,
        completedMessage: string,
    ) {
        if (this.prompt) {
            this._toastr.warning("An action is already running!");
            return;
        }

        if (this.toast) {
            this.toast.portal.destroy();
            this.toast = null;
        }

        this.prompt = this.promptService.loader(progressMessage);
        action
            .pipe(
                finalize(() => {
                    this.prompt.close();
                    this.prompt = null;
                }),
            )
            .subscribe(() => {
                this.toast = this._toastr.success(completedMessage);
            });
    }

    reloadSiteConfigurations() {
        const action = this.dataService.reloadSiteConfigurations();
        this.visualizeAction(
            action,
            "Reloading Site Configuration",
            "Site Configurations Reloaded",
        );
    }

    reloadDbSettings() {
        const action = this.dataService.reloadDbSettings();
        this.visualizeAction(action, "Reloading DB Settings", "DB Settings reloaded");
    }

    triggerNotifications() {
        const action = this.dataService.triggerNotifications();
        this.visualizeAction(action, "Triggering Notifications", "Notifications Triggered");
    }

    testScheduler() {
        const action = this.dataService.testScheduler();
        this.visualizeAction(action, "Triggering Test Scheduler", "Test Scheduler Triggered");
    }

    clearFluentIconsCache() {
        const action = this.dataService.clearFluentIconsCache();
        this.visualizeAction(action, "Clearing Fluent Icons Cache", "Fluent Icons Cache Cleared");
    }

    controlExecutionGenerateSourceDocs() {
        const action = this.dataService.controlExecutionGenerateSourceDocs();
        this.visualizeAction(action, "Generating source documents", "Source documents generated");
    }

    async migrateToIncidents() {
        const startMigration = await this.confirmMigrationToIncidents();
        if (startMigration) {
            await this.dataService.migrateToIncidents();

            this._toastr.success("Migration to incidents completed");
        } else {
            this._toastr.info("Migration to incidents cancelled");
        }
    }

    async confirmMigrationToIncidents(): Promise<boolean> {
        if (
            !(await this.promptService
                .confirm("Migrate to Incidents", "Are you sure you want to migrate to incidents?")
                .toPromise())
        ) {
            return false;
        }

        let confirmationNumber = this.getRandomInt(10000, 99999);
        let validation = await this.promptService
            .prompt({
                maxHeight: "375px",
                maxWidth: "440px",
                data: {
                    title: "Migrate to Incidents Security Check",
                    message: `To make sure this is not an accident, please enter your reason the following number: ${confirmationNumber}`,
                    fields: [
                        {
                            fieldType: GenericListFieldType.Number,
                            fieldName: "number",
                            prettyName: "Number",
                            defaultValue: 10000,
                            minimumValue: 10000,
                            maximumValue: 99999,
                            required: true,
                        },
                    ],
                },
            })
            .toPromise();

        if (!validation) {
            return false;
        }

        let number = validation.number;
        if (number != confirmationNumber) {
            this._toastr.warning("Invalid validation number");
            return false;
        }

        return true;
    }

    getRandomInt(min: number, max: number) {
        let x = max - min;
        let rnd = Math.floor(Math.random() * x);
        return rnd + min;
    }
}
